/**
 * @file Translates the game's event bus into PostHog events.
 *
 * This is the only file that knows both vocabularies, which is the point: no
 * system gains an analytics dependency, and the reporting schema can be
 * reshaped here without touching gameplay. It subscribes to the same bus the
 * HUD and the audio layer use, so it observes the real game rather than a
 * parallel set of hand-placed call sites that can drift.
 *
 * ## The volume rule
 *
 * A run fires tens of thousands of bus events — every shot, every hit, every
 * frame's interaction prompt. Sending those one-for-one would be both ruinous
 * and *less* informative than what this file does instead:
 *
 * - **Discrete decisions become events.** Starting a run, picking up a weapon,
 *   dying, clearing a floor: things a player chose or a moment that changed.
 * - **Repeated actions become counters**, folded into the `level_completed` and
 *   `run_ended` summaries. Accuracy per weapon, kills per archetype, damage
 *   taken, reloads, dry fires — every one is a property on a summary event, so
 *   one row answers "which gun do players actually win with" without a
 *   million-row scan.
 *
 * The two exceptions are deliberate: `player_damaged` is throttled rather than
 * dropped (because *where* damage comes from is a shape you want over time),
 * and `performance_sampled` is a slow heartbeat so a frame-rate cliff has a
 * timestamp instead of only a run average.
 */

import { Events } from '../core/EventBus.js';
import { PlayerControlled, Health, Weapon } from '../components/index.js';
import { ANALYTICS } from '../config/Analytics.js';

/** Frames per second below which a frame counts as jank. */
const JANK_FPS = 45;

/**
 * Ceiling on a single damage figure that will be summed into a total.
 *
 * The `oneShotKills` cheat does not deal a lot of damage — it passes
 * `Number.MAX_SAFE_INTEGER` as a *sentinel* meaning "remove this entity". Summed
 * naively that produced a `damage_dealt` of 9007199254740991 on the first live
 * run, which is not a large number so much as a broken one. Anything above this
 * bound is a sentinel, not a hit: the kill still counts, the damage does not.
 */
const MAX_PLAUSIBLE_DAMAGE = 100_000;

/**
 * @param {any} amount Raw damage figure off the bus.
 * @returns {number} A value safe to add to a running total.
 */
function damageValue(amount) {
  const value = Number(amount);
  if (!Number.isFinite(value) || value <= 0) return 0;
  return value > MAX_PLAUSIBLE_DAMAGE ? 0 : value;
}

/**
 * @param {any} entity Candidate entity.
 * @returns {boolean} True when this is the player.
 */
function isPlayer(entity) {
  return Boolean(entity?.has?.(PlayerControlled));
}

/**
 * Archetype key of an enemy entity, or null for anything else (the player, a
 * destructible barrel, an expired countdown's absent source).
 * @param {any} entity Candidate entity.
 * @returns {string | null}
 */
function archetypeOf(entity) {
  if (!entity || isPlayer(entity)) return null;
  return typeof entity.archetypeKey === 'string' ? entity.archetypeKey : null;
}

/**
 * @param {Record<string, number>} map Counter map.
 * @param {string | null | undefined} key Bucket.
 * @param {number} [amount] Increment.
 * @returns {void}
 */
function bump(map, key, amount = 1) {
  if (!key) return;
  map[key] = (map[key] ?? 0) + amount;
}

/**
 * @param {number} value Seconds.
 * @returns {number} Rounded to milliseconds, so properties stay readable.
 */
function seconds(value) {
  return Math.round(value * 1000) / 1000;
}

/**
 * Damaging impacts per trigger pull.
 *
 * Deliberately *not* called accuracy and deliberately not clamped to 1. A
 * shotgun fires 9 pellets per pull and an explosion damages everything in a
 * radius, so a good shot legitimately lands more than one hit. Above 1 means
 * multi-hit efficiency, below 1 means missing — both are readable, which a
 * clamped "accuracy" would not be.
 *
 * @param {number} hits Damaging impacts landed.
 * @param {number} shots Trigger pulls.
 * @returns {number} Hits per shot, zero when nothing was fired.
 */
function hitsPerShot(hits, shots) {
  return shots > 0 ? Math.round((hits / shots) * 1000) / 1000 : 0;
}

export class AnalyticsBridge {
  /**
   * @param {import('../core/Context.js').GameContext} ctx Shared game context.
   * @param {import('./Analytics.js').Analytics} analytics Reporting client.
   * @param {object} [options] Setup.
   * @param {typeof ANALYTICS} [options.config] Settings override.
   * @param {() => number} [options.now] Clock in milliseconds, for tests.
   */
  constructor(ctx, analytics, { config = ANALYTICS, now } = {}) {
    /** @type {import('../core/Context.js').GameContext} @private */
    this._ctx = ctx;
    /** @type {import('./Analytics.js').Analytics} @private */
    this._analytics = analytics;
    /** @type {typeof ANALYTICS} @private */
    this._config = config;
    /** @type {() => number} @private */
    this._now = now ?? (() => (typeof performance !== 'undefined' ? performance.now() : Date.now()));

    /** @type {(() => void)[]} @private */
    this._subscriptions = [];
    /** @type {(() => void)[]} Non-bus listeners (DOM). @private */
    this._teardown = [];

    /** @type {number} @private */
    this._sessionStart = this._now();
    /** @type {string} @private */
    this._state = '';
    /** @type {number} When the current state was entered. @private */
    this._stateSince = this._sessionStart;
    /** @type {number} When LOADING began, for the generation cost. @private */
    this._loadStart = 0;
    /** @type {number} @private */
    this._pauseStart = 0;
    /**
     * True between entering LOADING and `LEVEL_READY`. Weapons equipped inside
     * that window are spawn equips, not player choices, so they are folded into
     * `level_started` as `starting_weapon` rather than reported on their own.
     * @type {boolean}
     * @private
     */
    this._loadingLevel = false;

    /** @type {ReturnType<typeof AnalyticsBridge.newRun> | null} @private */
    this._run = null;
    /** @type {ReturnType<typeof AnalyticsBridge.newLevel> | null} @private */
    this._level = null;
    /** @type {{ wave: number, startedAt: number, kills: number } | null} @private */
    this._wave = null;

    /** @type {string | null} Weapon the player is holding right now. @private */
    this._weapon = null;
    /** @type {{ type: string | null, archetype: string | null }} @private */
    this._lastPlayerHit = { type: null, archetype: null };
    /** @type {number} @private */
    this._lastDamageEventAt = -Infinity;
    /** @type {number} Seconds until the accumulated fps heartbeat. @private */
    this._sampleCountdown = config.performanceSampleSeconds;
    /** @type {number} @private */
    this._gamepadCountdown = config.gamepadProbeSeconds;
    /** @type {Set<string>} Input methods already reported. @private */
    this._inputMethods = new Set();
    /** @type {number} Runs completed this page load. @private */
    this._runOrdinal = 0;
  }

  /**
   * Fresh per-run accumulator.
   * @param {string} id Run id.
   * @param {number | null} seed Run seed.
   * @param {number} startedAt Clock reading.
   * @returns {object}
   */
  static newRun(id, seed, startedAt) {
    return {
      id,
      seed,
      startedAt,
      /** Floors reached, including the one in progress. */
      levels: 0,
      /** Floors actually cleared through the exit. */
      cleared: 0,
      restarts: 0,
      kills: 0,
      score: 0,
      wave: 1,
      shots: 0,
      hitsLanded: 0,
      damageDealt: 0,
      damageTaken: 0,
      pickups: 0,
      timePaused: 0,
      /** @type {Record<string, number>} */
      killsByArchetype: {},
      /** @type {Record<string, number>} */
      killsByWeapon: {},
      /** @type {Record<string, number>} */
      weaponsUsed: {}
    };
  }

  /**
   * Fresh per-level accumulator.
   * @param {number} index Floor number.
   * @param {number | null} seed Level seed.
   * @param {string | null} name Level name.
   * @param {string | null} theme Theme key.
   * @param {number} startedAt Clock reading.
   * @returns {object}
   */
  static newLevel(index, seed, name, theme, startedAt) {
    return {
      index,
      seed,
      name,
      theme,
      startedAt,
      waves: 0,
      kills: 0,
      score: 0,
      shots: 0,
      hitsLanded: 0,
      hitscanMisses: 0,
      damageDealt: 0,
      damageTaken: 0,
      hitsTaken: 0,
      pickups: 0,
      reloads: 0,
      reloadsInterrupted: 0,
      dryFires: 0,
      throws: 0,
      drops: 0,
      explosions: 0,
      contactHits: 0,
      /** @type {Record<string, number>} */
      killsByArchetype: {},
      /** @type {Record<string, number>} */
      killsByWeapon: {},
      /** @type {Record<string, number>} */
      shotsByWeapon: {},
      /** @type {Record<string, number>} */
      hitsByWeapon: {},
      /** @type {Record<string, number>} */
      damageByWeapon: {},
      /** @type {Record<string, number>} */
      damageByArchetype: {},
      fps: { sum: 0, count: 0, min: Infinity, jank: 0 }
    };
  }

  /**
   * Subscribes to everything and reports `game_loaded`.
   * @returns {void}
   */
  attach() {
    this._captureLoaded();
    this._watchInput();
    this._subscribeLifecycle();
    this._subscribeCombat();
    this._subscribeLoadout();
    this._subscribeUi();
  }

  /**
   * Per-frame work: the frame-rate heartbeat and the gamepad probe. Called from
   * `Game._update`, so it ticks while paused too — which is what makes a long
   * pause visible as a gap in the heartbeat rather than as nothing at all.
   * @param {number} dt Delta time in seconds.
   * @returns {void}
   */
  update(dt) {
    if (!this._analytics.enabled) return;

    const fps = this._ctx.time?.fps ?? 0;
    if (this._level && this._state === 'playing' && fps > 0) {
      const stats = this._level.fps;
      stats.sum += fps;
      stats.count++;
      if (fps < stats.min) stats.min = fps;
      if (fps < JANK_FPS) stats.jank++;
    }

    this._sampleCountdown -= dt;
    if (this._sampleCountdown <= 0) {
      this._sampleCountdown = this._config.performanceSampleSeconds;
      if (this._state === 'playing') this._capturePerformance(fps);
    }

    if (this._inputMethods.has('gamepad')) return;
    this._gamepadCountdown -= dt;
    if (this._gamepadCountdown > 0) return;
    this._gamepadCountdown = this._config.gamepadProbeSeconds;
    const pads = navigator.getGamepads?.() ?? [];
    for (const pad of pads) {
      if (pad?.connected) {
        this._reportInput('gamepad', { gamepad_id: pad.id });
        return;
      }
    }
  }

  /**
   * Closes an open run as abandoned — the tab is going away — and unsubscribes.
   * @returns {void}
   */
  dispose() {
    this._endRun('abandoned');
    for (const unsubscribe of this._subscriptions) unsubscribe();
    this._subscriptions.length = 0;
    for (const off of this._teardown) off();
    this._teardown.length = 0;
  }

  // --- Wiring ---------------------------------------------------------------

  /** @returns {void} @private */
  _subscribeLifecycle() {
    const { events } = this._ctx;
    this._subscriptions.push(
      events.on(Events.STATE_CHANGED, (payload) => this._onStateChanged(payload)),
      events.on(Events.LEVEL_READY, (payload) => this._onLevelReady(payload)),
      events.on(Events.LEVEL_COMPLETE, () => this._onLevelComplete()),
      events.on(Events.WAVE_STARTED, (payload) => this._onWaveStarted(payload)),
      events.on(Events.WAVE_CLEARED, (payload) => this._onWaveCleared(payload)),
      events.on(Events.SCORE_CHANGED, ({ score, wave, kills }) => {
        // Fires on every kill, so it is state to remember, never an event to
        // send. It is also the only place the director's totals are published.
        if (this._level) this._level.score = score;
        if (this._run) {
          this._run.score = score;
          this._run.wave = wave;
          this._run.kills = kills;
        }
      })
    );
  }

  /** @returns {void} @private */
  _subscribeCombat() {
    const { events } = this._ctx;
    this._subscriptions.push(
      events.on(Events.DIED, (payload) => this._onDied(payload)),
      events.on(Events.DAMAGE_APPLIED, (payload) => this._onDamageApplied(payload)),
      events.on(Events.WEAPON_FIRED, ({ owner, weapon }) => {
        if (!isPlayer(owner) || !this._level) return;
        this._level.shots++;
        bump(this._level.shotsByWeapon, weapon?.key ?? this._weapon);
        if (this._run) this._run.shots++;
      }),
      // Landed hits are counted from DAMAGE_APPLIED, not from WEAPON_HIT.
      // WEAPON_HIT only exists on the hitscan path (`WeaponSystem._traceHitscan`)
      // — the shotgun, the rocket launcher and every other projectile weapon
      // never emit it, which made `shots_hit` read 0 on a run that dealt 100
      // damage. Same for WEAPON_MISSED, so its counter says hitscan in the name.
      events.on(Events.WEAPON_MISSED, ({ owner }) => {
        if (!isPlayer(owner) || !this._level) return;
        this._level.hitscanMisses++;
      }),
      events.on(Events.EXPLOSION, () => {
        if (this._level) this._level.explosions++;
      }),
      events.on(Events.ENEMY_MELEE_ATTACK, ({ target }) => {
        if (isPlayer(target) && this._level) this._level.contactHits++;
      })
    );
  }

  /** @returns {void} @private */
  _subscribeLoadout() {
    const { events } = this._ctx;
    this._subscriptions.push(
      events.on(Events.WEAPON_EQUIPPED, ({ weapon, owner }) => {
        if (!isPlayer(owner)) return;
        const previous = this._weapon;
        this._weapon = weapon?.key ?? null;
        if (this._weapon) bump(this._run?.weaponsUsed ?? {}, this._weapon);
        this._analytics.register({ weapon: this._weapon });
        // A spawn equip is not a player choice; `level_started` already carries
        // the loadout as `starting_weapon`, read straight off the player.
        if (this._loadingLevel) return;
        this._capture('weapon_equipped', {
          weapon_key: this._weapon,
          weapon_name: weapon?.name ?? null,
          previous_weapon_key: previous
        });
      }),
      events.on(Events.WEAPON_THROWN, ({ weapon, source }) => {
        if (!isPlayer(source)) return;
        if (this._level) this._level.throws++;
        this._capture('weapon_thrown', { weapon_key: weapon?.key ?? null });
      }),
      events.on(Events.WEAPON_DROPPED, ({ entity, weapon }) => {
        if (!isPlayer(entity)) return;
        if (this._level) this._level.drops++;
        this._capture('weapon_dropped', { weapon_key: weapon?.key ?? null });
      }),
      events.on(Events.PICKUP_COLLECTED, ({ entity, key, definition }) => {
        if (!isPlayer(entity)) return;
        if (this._level) this._level.pickups++;
        if (this._run) this._run.pickups++;
        this._capture('powerup_collected', {
          powerup_key: key ?? null,
          powerup_kind: definition?.kind ?? null,
          powerup_name: definition?.name ?? null
        });
      }),
      events.on(Events.RELOAD_COMPLETED, () => {
        if (this._level) this._level.reloads++;
      }),
      events.on(Events.RELOAD_INTERRUPTED, () => {
        if (this._level) this._level.reloadsInterrupted++;
      }),
      events.on(Events.WEAPON_EMPTY_TRIGGER, ({ owner }) => {
        // A dry fire is the clearest signal a magazine size is wrong.
        if (isPlayer(owner) && this._level) this._level.dryFires++;
      })
    );
  }

  /** @returns {void} @private */
  _subscribeUi() {
    const { events } = this._ctx;
    this._subscriptions.push(
      events.on(Events.MENU_NAV_ACTION, ({ action }) => this._capture('menu_nav_clicked', { action })),
      events.on(Events.CONFIRM_QUIT_PROMPT, ({ visible }) => {
        if (visible) this._capture('quit_prompt_shown');
      }),
      events.on(Events.CONFIRM_QUIT_RESPONSE, ({ confirmed }) =>
        this._capture('quit_prompt_answered', { confirmed: Boolean(confirmed) })
      ),
      events.on(Events.SETTINGS_CHANGED, ({ setting, value }) =>
        this._capture('settings_changed', { setting, value })
      ),
      events.on(Events.DEBUG_TOGGLED, ({ open }) => this._capture('debug_panel_toggled', { open: Boolean(open) }))
    );
  }

  /**
   * Input-method detection. Kept here rather than in `InputManager` so the
   * input layer keeps no analytics dependency; the gamepad half lives in
   * `update`, since a pad announces itself by being polled.
   * @returns {void}
   * @private
   */
  _watchInput() {
    if (typeof window === 'undefined') return;
    /** @type {[string, string][]} */
    const pairs = [
      ['keydown', 'keyboard'],
      ['pointerdown', 'pointer']
    ];
    for (const [domEvent, method] of pairs) {
      const handler = () => this._reportInput(method);
      window.addEventListener(domEvent, handler, { passive: true, capture: true });
      this._teardown.push(() => window.removeEventListener(domEvent, handler, { capture: true }));
    }
  }

  // --- Handlers -------------------------------------------------------------

  /**
   * @param {{ state: string, previous: string }} payload Transition.
   * @returns {void}
   * @private
   */
  _onStateChanged({ state, previous }) {
    const now = this._now();
    const dwell = seconds((now - this._stateSince) / 1000);
    this._stateSince = now;
    this._state = state;
    this._analytics.register({ game_state: state });

    this._capture('game_state_changed', {
      state,
      previous_state: previous || null,
      previous_state_duration_s: dwell
    });

    if (state === 'menu') {
      // Reaching the title screen from anywhere inside a run *is* the quit:
      // `MenuState.enter` tears the level down, so nothing else will report it.
      if (this._run) this._endRun(previous === 'gameOver' ? 'death' : 'quit');
      this._capture('menu_viewed', { from_state: previous || null });
      return;
    }

    if (state === 'loading') {
      this._loadStart = now;
      this._loadingLevel = true;
      return;
    }

    if (state === 'paused') {
      this._pauseStart = now;
      this._capture('game_paused', {
        level_index: this._level?.index ?? null,
        wave: this._run?.wave ?? null,
        level_duration_s: this._level ? seconds((now - this._level.startedAt) / 1000) : null
      });
      return;
    }

    if (previous === 'paused' && this._pauseStart > 0) {
      const paused = (now - this._pauseStart) / 1000;
      this._pauseStart = 0;
      if (this._run) this._run.timePaused += paused;
      this._capture('game_resumed', { pause_duration_s: seconds(paused) });
    }
  }

  /**
   * A floor finished generating and is on screen. This is the only reliable
   * "a level began" signal: `LOADING` can retry generation several times.
   *
   * @param {{ levelIndex: number, seed: number, name: string | null, themeKey: string | null }} payload Level.
   * @returns {void}
   * @private
   */
  _onLevelReady({ levelIndex, seed, name, themeKey }) {
    const now = this._now();
    this._loadingLevel = false;
    const loadDuration = this._loadStart > 0 ? seconds((now - this._loadStart) / 1000) : null;

    // Read the loadout off the freshly spawned player rather than waiting for
    // an equip event: `createPlayer` equips without an event bus, so the spawn
    // weapon is never announced. Trusting the last `WEAPON_EQUIPPED` instead
    // reported null on the first floor and leaked the *previous* run's weapon
    // on the second.
    this._weapon = this._ctx.player?.get?.(Weapon)?.key ?? null;
    this._analytics.register({ weapon: this._weapon });

    // A new run is a new run seed. Restarting the same floor keeps both, which
    // is how the debug window's "restart level" stays distinguishable.
    const runSeed = this._ctx.runSeed ?? null;
    const restarted = Boolean(this._run && this._run.seed === runSeed && this._level?.index === levelIndex);

    if (!this._run || this._run.seed !== runSeed) {
      if (this._run) this._endRun('abandoned');
      this._startRun(runSeed, now);
    }

    if (restarted && this._run) this._run.restarts++;

    // The previous floor never reported a completion (died, or restarted), so
    // close it out before opening the next one — no level may be left dangling.
    if (this._level && !restarted) this._flushLevel('abandoned');

    this._level = AnalyticsBridge.newLevel(levelIndex, seed ?? null, name ?? null, themeKey ?? null, now);
    if (this._run) this._run.levels++;
    this._wave = null;

    this._analytics.register({
      level_index: levelIndex,
      level_name: name ?? null,
      level_theme: themeKey ?? null
    });

    this._capture(restarted ? 'level_restarted' : 'level_started', {
      level_index: levelIndex,
      level_seed: seed ?? null,
      level_name: name ?? null,
      level_theme: themeKey ?? null,
      starting_weapon: this._weapon,
      load_duration_s: loadDuration,
      run_level_ordinal: this._run?.levels ?? 1,
      population_mode: this._populationMode()
    });
  }

  /** @returns {void} @private */
  _onLevelComplete() {
    if (this._run) this._run.cleared++;
    this._flushLevel('completed');
  }

  /**
   * @param {{ wave: number, count: number }} payload Wave roster.
   * @returns {void}
   * @private
   */
  _onWaveStarted({ wave, count }) {
    this._wave = { wave, startedAt: this._now(), kills: 0 };
    if (this._level) this._level.waves++;
    this._analytics.register({ wave });
    this._capture('wave_started', { wave, enemy_count: count ?? null, level_index: this._level?.index ?? null });
  }

  /**
   * @param {{ wave: number }} payload Cleared wave.
   * @returns {void}
   * @private
   */
  _onWaveCleared({ wave }) {
    const duration = this._wave ? seconds((this._now() - this._wave.startedAt) / 1000) : null;
    this._capture('wave_cleared', {
      wave,
      duration_s: duration,
      kills_in_wave: this._wave?.kills ?? null,
      level_index: this._level?.index ?? null
    });
    this._wave = null;
  }

  /**
   * @param {{ entity: any, source: any }} payload Death.
   * @returns {void}
   * @private
   */
  _onDied({ entity, source }) {
    if (isPlayer(entity)) {
      this._onPlayerDied(source);
      return;
    }
    const archetype = archetypeOf(entity);
    if (!archetype) return; // A barrel, a crate: counted as damage, not a kill.

    const weapon = isPlayer(source) ? (this._weapon ?? 'unknown') : null;
    if (this._level) {
      this._level.kills++;
      bump(this._level.killsByArchetype, archetype);
      bump(this._level.killsByWeapon, weapon);
    }
    if (this._run) {
      bump(this._run.killsByArchetype, archetype);
      bump(this._run.killsByWeapon, weapon);
    }
    if (this._wave) this._wave.kills++;
  }

  /**
   * @param {any} source Killer, or null when the countdown ran out.
   * @returns {void}
   * @private
   */
  _onPlayerDied(source) {
    const now = this._now();
    // The countdown *is* the game: hitting zero is a null-source death, and it
    // is the only one, so the distinction needs no heuristic.
    const cause = source == null ? 'countdown' : isPlayer(source) ? 'self' : 'enemy';
    this._capture('player_died', {
      cause,
      killer_archetype: archetypeOf(source),
      last_damage_type: this._lastPlayerHit.type,
      last_damage_archetype: this._lastPlayerHit.archetype,
      weapon_key: this._weapon,
      level_index: this._level?.index ?? null,
      wave: this._run?.wave ?? null,
      score: this._run?.score ?? 0,
      kills: this._run?.kills ?? 0,
      level_duration_s: this._level ? seconds((now - this._level.startedAt) / 1000) : null,
      run_duration_s: this._run ? seconds((now - this._run.startedAt) / 1000) : null,
      damage_taken: this._level?.damageTaken ?? 0,
      hits_per_shot: hitsPerShot(this._level?.hitsLanded ?? 0, this._level?.shots ?? 0)
    });
    this._flushLevel('died');
    this._endRun('death');
  }

  /**
   * @param {{ target: any, amount: number, source: any, type?: string }} payload Applied damage.
   * @returns {void}
   * @private
   */
  _onDamageApplied({ target, amount, source, type }) {
    const value = damageValue(amount);

    if (isPlayer(target)) {
      const archetype = archetypeOf(source);
      this._lastPlayerHit.type = type ?? null;
      this._lastPlayerHit.archetype = archetype;
      if (this._level) {
        this._level.damageTaken += value;
        this._level.hitsTaken++;
        bump(this._level.damageByArchetype, archetype ?? 'environment', value);
      }
      if (this._run) this._run.damageTaken += value;

      const now = this._now();
      if ((now - this._lastDamageEventAt) / 1000 < this._config.playerDamageMinIntervalSeconds) return;
      this._lastDamageEventAt = now;
      this._capture('player_damaged', {
        amount: value,
        damage_type: type ?? null,
        source_archetype: archetype,
        self_inflicted: isPlayer(source),
        countdown_remaining_s: this._countdownRemaining(),
        level_index: this._level?.index ?? null,
        wave: this._run?.wave ?? null
      });
      return;
    }

    if (isPlayer(source)) {
      // The only signal every weapon path shares — hitscan, projectile, melee,
      // explosion, burn — and the only one that survives shields and i-frames.
      if (this._level) {
        this._level.damageDealt += value;
        this._level.hitsLanded++;
        bump(this._level.hitsByWeapon, this._weapon);
        bump(this._level.damageByWeapon, this._weapon, value);
      }
      if (this._run) {
        this._run.damageDealt += value;
        this._run.hitsLanded++;
      }
    }
  }

  // --- Reporting ------------------------------------------------------------

  /**
   * @param {number | null} seed Run seed.
   * @param {number} now Clock reading.
   * @returns {void}
   * @private
   */
  _startRun(seed, now) {
    this._runOrdinal++;
    const id = `${seed ?? 'noseed'}-${Math.round(now)}`;
    this._run = AnalyticsBridge.newRun(id, seed, now);
    this._analytics.register({ run_id: id, run_seed: seed });
    this._capture('run_started', {
      run_seed: seed,
      run_ordinal_in_session: this._runOrdinal,
      starting_weapon: this._weapon
    });
  }

  /**
   * Emits `run_ended` and updates the person record. Idempotent: a run can only
   * end once, so the death path and the subsequent `menu` transition cannot
   * both report it.
   *
   * @param {'death' | 'quit' | 'abandoned'} outcome How the run finished.
   * @returns {void}
   * @private
   */
  _endRun(outcome) {
    const run = this._run;
    if (!run) return;
    this._run = null;
    // A floor still open when the run ends (quit from pause) has to be closed
    // first, or its counters vanish.
    if (this._level) this._flushLevel(outcome === 'death' ? 'died' : 'abandoned');

    const duration = seconds((this._now() - run.startedAt) / 1000);
    this._capture('run_ended', {
      outcome,
      run_seed: run.seed,
      run_duration_s: duration,
      active_duration_s: seconds(Math.max(0, duration - run.timePaused)),
      paused_duration_s: seconds(run.timePaused),
      levels_reached: run.levels,
      levels_cleared: run.cleared,
      deepest_level: run.levels,
      population_mode: this._populationMode(),
      level_restarts: run.restarts,
      waves_reached: run.wave,
      score: run.score,
      kills: run.kills,
      shots_fired: run.shots,
      hits_landed: run.hitsLanded,
      hits_per_shot: hitsPerShot(run.hitsLanded, run.shots),
      damage_dealt: Math.round(run.damageDealt),
      damage_taken: Math.round(run.damageTaken),
      powerups_collected: run.pickups,
      kills_by_archetype: run.killsByArchetype,
      kills_by_weapon: run.killsByWeapon,
      weapons_used: Object.keys(run.weaponsUsed),
      weapon_count: Object.keys(run.weaponsUsed).length
    });

    this._analytics.person(
      {
        last_run_outcome: outcome,
        last_run_score: run.score,
        last_run_levels_cleared: run.cleared
      },
      { first_run_at: new Date().toISOString() }
    );
    this._analytics.unregister('run_id');
    this._analytics.unregister('run_seed');
  }

  /**
   * Emits `level_completed` with the whole per-floor summary and clears it.
   * @param {'completed' | 'died' | 'abandoned'} outcome How the floor ended.
   * @returns {void}
   * @private
   */
  _flushLevel(outcome) {
    const level = this._level;
    if (!level) return;
    this._level = null;

    const { fps } = level;
    this._capture('level_completed', {
      outcome,
      level_index: level.index,
      level_seed: level.seed,
      level_name: level.name,
      level_theme: level.theme,
      population_mode: this._populationMode(),
      duration_s: seconds((this._now() - level.startedAt) / 1000),
      waves: level.waves,
      kills: level.kills,
      score: level.score,
      shots_fired: level.shots,
      hits_landed: level.hitsLanded,
      hitscan_misses: level.hitscanMisses,
      hits_per_shot: hitsPerShot(level.hitsLanded, level.shots),
      damage_dealt: Math.round(level.damageDealt),
      damage_taken: Math.round(level.damageTaken),
      hits_taken: level.hitsTaken,
      contact_hits_taken: level.contactHits,
      powerups_collected: level.pickups,
      reloads: level.reloads,
      reloads_interrupted: level.reloadsInterrupted,
      dry_fires: level.dryFires,
      weapons_thrown: level.throws,
      weapons_dropped: level.drops,
      explosions: level.explosions,
      kills_by_archetype: level.killsByArchetype,
      kills_by_weapon: level.killsByWeapon,
      shots_by_weapon: level.shotsByWeapon,
      hits_by_weapon: level.hitsByWeapon,
      damage_by_weapon: level.damageByWeapon,
      damage_taken_by_archetype: level.damageByArchetype,
      fps_avg: fps.count > 0 ? Math.round(fps.sum / fps.count) : null,
      fps_min: Number.isFinite(fps.min) ? Math.round(fps.min) : null,
      jank_ratio: fps.count > 0 ? Math.round((fps.jank / fps.count) * 1000) / 1000 : null
    });

    this._analytics.unregister('level_index');
    this._analytics.unregister('level_name');
    this._analytics.unregister('level_theme');
    this._analytics.unregister('wave');
  }

  /**
   * One-shot report of what this player's machine and build actually are, so a
   * frame-rate complaint has a GPU attached to it.
   * @returns {void}
   * @private
   */
  _captureLoaded() {
    /** @type {Record<string, any>} */
    const properties = {
      game: 'countdown',
      build_mode: import.meta.env?.MODE ?? null,
      base_path: import.meta.env?.BASE_URL ?? null,
      screen_width: typeof window !== 'undefined' ? window.screen?.width ?? null : null,
      screen_height: typeof window !== 'undefined' ? window.screen?.height ?? null : null,
      viewport_width: typeof window !== 'undefined' ? window.innerWidth : null,
      viewport_height: typeof window !== 'undefined' ? window.innerHeight : null,
      device_pixel_ratio: typeof window !== 'undefined' ? window.devicePixelRatio : null,
      cpu_threads: typeof navigator !== 'undefined' ? navigator.hardwareConcurrency ?? null : null,
      device_memory_gb: typeof navigator !== 'undefined' ? /** @type {any} */ (navigator).deviceMemory ?? null : null,
      touch_capable: typeof navigator !== 'undefined' ? (navigator.maxTouchPoints ?? 0) > 0 : null,
      reduced_motion:
        typeof window !== 'undefined'
          ? Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches)
          : null,
      shadows_enabled: Boolean(this._ctx.config?.renderer?.shadows),
      post_processing_enabled: Boolean(this._ctx.config?.post?.enabled),
      max_pixel_ratio: this._ctx.config?.renderer?.maxPixelRatio ?? null
    };
    Object.assign(properties, this._gpuInfo());

    // The machine is a property of the *player*, not of one page load: it is
    // what lets "frame rate by GPU" and "who is on an integrated card" be a
    // grouping rather than a join back onto this one event. Sent as `$set` on
    // the event itself so it costs no extra request.
    properties.$set = {
      gpu_renderer: properties.gpu_renderer ?? null,
      gpu_vendor: properties.gpu_vendor ?? null,
      cpu_threads: properties.cpu_threads,
      device_memory_gb: properties.device_memory_gb,
      screen_width: properties.screen_width,
      screen_height: properties.screen_height,
      device_pixel_ratio: properties.device_pixel_ratio,
      touch_capable: properties.touch_capable
    };
    this._capture('game_loaded', properties);
  }

  /**
   * GPU strings, read from the renderer's own context so no second WebGL
   * context is created just to ask.
   * @returns {Record<string, any>}
   * @private
   */
  _gpuInfo() {
    try {
      const gl = /** @type {any} */ (this._ctx.renderer)?.instance?.getContext?.();
      if (!gl) return {};
      const info = gl.getExtension('WEBGL_debug_renderer_info');
      return {
        gpu_renderer: info ? gl.getParameter(info.UNMASKED_RENDERER_WEBGL) : null,
        gpu_vendor: info ? gl.getParameter(info.UNMASKED_VENDOR_WEBGL) : null,
        webgl_version: gl.getParameter(gl.VERSION),
        max_texture_size: gl.getParameter(gl.MAX_TEXTURE_SIZE)
      };
    } catch {
      // A masked or lost context is not worth a broken load event.
      return {};
    }
  }

  /**
   * @param {number} fps Current frame rate.
   * @returns {void}
   * @private
   */
  _capturePerformance(fps) {
    const stats = this._level?.fps;
    this._capture('performance_sampled', {
      fps: Math.round(fps),
      fps_avg: stats && stats.count > 0 ? Math.round(stats.sum / stats.count) : null,
      fps_min: stats && Number.isFinite(stats.min) ? Math.round(stats.min) : null,
      jank_ratio: stats && stats.count > 0 ? Math.round((stats.jank / stats.count) * 1000) / 1000 : null,
      entities: this._ctx.world?.entities?.length ?? null,
      level_index: this._level?.index ?? null,
      wave: this._run?.wave ?? null,
      session_duration_s: seconds((this._now() - this._sessionStart) / 1000)
    });
  }

  /**
   * @param {string} method `keyboard`, `pointer` or `gamepad`.
   * @param {Record<string, any>} [properties] Extra detail.
   * @returns {void}
   * @private
   */
  _reportInput(method, properties = {}) {
    if (this._inputMethods.has(method)) return;
    this._inputMethods.add(method);
    this._analytics.register({ input_methods: [...this._inputMethods] });
    this._capture('input_method_detected', { method, ...properties });
  }

  /**
   * How this floor is populated, and therefore whether the wave fields on a
   * summary mean anything.
   *
   * The shipped default is `prepopulated`, in which `WaveSystem` returns early
   * and never emits `WAVE_STARTED` — so `waves` is legitimately 0 and progress
   * is measured by kills and by reaching the exit. Without this label on every
   * summary, a zero in the wave column is indistinguishable from a wave-mode
   * floor where the player cleared nothing.
   *
   * @returns {string} `prepopulated`, `waves` or `both`.
   * @private
   */
  _populationMode() {
    return this._ctx.config?.dungeon?.population?.mode ?? 'waves';
  }

  /**
   * @returns {number | null} Seconds left on the player's countdown.
   * @private
   */
  _countdownRemaining() {
    const health = this._ctx.player?.get?.(Health);
    return health ? seconds(health.killTimer ?? 0) : null;
  }

  /**
   * @param {string} event Event name.
   * @param {Record<string, any>} [properties] Properties.
   * @returns {void}
   * @private
   */
  _capture(event, properties) {
    this._analytics.capture(event, properties);
  }
}
